import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { UserPlus, X } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { LinkedUser } from "@/types/linkedUsers";
import { useAuth } from "@/providers/AuthProvider";
import { useUsers } from "@/hooks/useUsers";

interface LinkedUsersManagerProps {
	linkedUsers: LinkedUser[];
	onLinkUser: (userId: string) => void;
	onUnlinkUser: (userId: string) => void;
	isLoading?: boolean;
	title?: string;
	description?: string;
}

export const LinkedUsersManager = ({
	linkedUsers,
	onLinkUser,
	onUnlinkUser,
	isLoading = false,
	title = "Linked Users",
	description = "Users who have access to this item",
}: LinkedUsersManagerProps) => {
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const { user } = useAuth();
	const { data: allUsers = [], isLoading: isLoadingUsers } = useUsers();
	
	// Filter out already linked users
	const availableUsers = allUsers.filter(
		(u) => !linkedUsers.some((lu) => lu.user_id === u.id) && u.id !== user?.id
	);
	
	const handleLinkUser = (userId: string) => {
		onLinkUser(userId);
		setIsDialogOpen(false);
	};
	
	const getUserInitials = (firstName: string | null, lastName: string | null) => {
		const first = firstName?.[0] || "";
		const last = lastName?.[0] || "";
		return (first + last).toUpperCase() || "?";
	};
	
	return <Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<div>
						<CardTitle className="text-base">{title}</CardTitle>
						<CardDescription className="text-sm">{description}</CardDescription>
					</div>
					<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
						<DialogTrigger asChild>
							<Button variant="outline" size="sm" disabled={isLoading}>
								<UserPlus className="h-4 w-4 mr-2" />
								Add User
							</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Add User</DialogTitle>
								<DialogDescription>
									Select a user from your organization to link to this item.
								</DialogDescription>
							</DialogHeader>
							<div className="space-y-2 max-h-[400px] overflow-y-auto">
								{isLoadingUsers ? (
									<div className="text-center py-4 text-muted-foreground">
										Loading users...
									</div>
								) : availableUsers.length === 0 ? (
									<div className="text-center py-4 text-muted-foreground">
										No available users to add
									</div>
								) : (
									availableUsers.map((u) => (
										<Button
											key={u.id}
											variant="ghost"
											className="w-full justify-start"
											onClick={() => handleLinkUser(u.id)}
										>
											<Avatar className="h-8 w-8 mr-3">
												<AvatarImage src={u.avatar_url || undefined} />
												<AvatarFallback>
													{getUserInitials(u.first_name, u.last_name)}
												</AvatarFallback>
											</Avatar>
											<div className="text-left">
												<div className="font-medium">
													{u.first_name} {u.last_name}
												</div>
												<div className="text-sm text-muted-foreground">
													{u.email}
												</div>
											</div>
										</Button>
									))
								)}
							</div>
						</DialogContent>
					</Dialog>
				</div>
			</CardHeader>
			<CardContent>
				{linkedUsers.length === 0 ? (
					<div className="text-center py-4 text-muted-foreground text-sm">
						No linked users yet
					</div>
				) : (
					<div className="space-y-3">
						{linkedUsers.map((linkedUser) => {
							const isCurrentUser = linkedUser.user_id === user?.id;
							
							return <div
									key={linkedUser.user_id}
									className="flex items-center justify-between p-3 rounded-sm border bg-card"
								>
									<div className="flex items-center gap-3">
										<Avatar className="h-10 w-10">
											<AvatarImage src={linkedUser.avatar_url || undefined} />
											<AvatarFallback>
												{getUserInitials(
													linkedUser.first_name,
													linkedUser.last_name
												)}
											</AvatarFallback>
										</Avatar>
										<div>
											<div className="font-medium flex items-center gap-2">
												{linkedUser.first_name} {linkedUser.last_name}
												{isCurrentUser && (
													<Badge variant="secondary" className="text-xs">
														You
													</Badge>
												)}
											</div>
											<div className="text-sm text-muted-foreground">
												{linkedUser.email}
											</div>
											<div className="text-xs text-muted-foreground">
												Added {new Date(linkedUser.linked_at).toLocaleDateString()}
											</div>
										</div>
									</div>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => onUnlinkUser(linkedUser.user_id)}
										disabled={isLoading}
										title={isCurrentUser ? "Remove yourself" : "Remove user"}
									>
										<X className="h-4 w-4" />
									</Button>
								</div>;
						})}
					</div>
				)}
			</CardContent>
		</Card>;
};
